import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';
import { useManagerViewers } from '../../hooks/useManagerHierarchy';

interface ManagerVisibilityInfoProps {
  /** The user_id of the entity owner */
  ownerId: string | undefined;
  /** The module for visibility settings lookup (e.g., 'sales') */
  module: string;
}

/**
 * Read-only section showing which managers can view an entity
 * via the manager hierarchy visibility feature.
 */
export function ManagerVisibilityInfo({ ownerId, module }: ManagerVisibilityInfoProps) {
  const { t } = useTranslation('common');
  const { data } = useManagerViewers(ownerId, module);

  // Don't render if feature is disabled or no viewers
  if (!data?.enabled || !data?.data || data.data.length === 0) {
    return null;
  }

  return (
    <>
    <Separator />
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-medium text-muted-foreground">
          {t('team.managersCanView')}
        </p>
      </div>
      <div className="space-y-1 opacity-75">
        {data.data.map((viewer) => (
          <div
            key={viewer.user_id}
            className="flex items-center gap-2 py-2"
          >
            <Avatar className="h-8 w-8">
              {viewer.avatar_url && <AvatarImage src={viewer.avatar_url} />}
              <AvatarFallback className="text-xs">
                {viewer.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{viewer.name}</p>
            </div>
            <Badge variant="secondary">
              {t('sharing.relations.viewer')}
            </Badge>
          </div>
        ))}
      </div>
    </div>
    </>
  );
}
